import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Shield, CheckCircle2 } from "lucide-react";

const CONSENT_STORAGE_KEY = "privacy-policy-consent";
const CURRENT_POLICY_VERSION = "2026-06";

interface PrivacyConsentDialogProps {
  open?: boolean;
  onConsent?: () => void;
  blocking?: boolean;
}

export function PrivacyConsentDialog({
  open: controlledOpen,
  onConsent,
  blocking = false,
}: PrivacyConsentDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  useEffect(() => {
    if (isControlled) return;
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      setInternalOpen(true);
    }
  }, [isControlled]);

  function handleAccept() {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
      version: CURRENT_POLICY_VERSION,
      acceptedAt: new Date().toISOString(),
    }));
    if (isControlled) {
      onConsent?.();
    } else {
      setInternalOpen(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={blocking ? () => {} : undefined}>
      <DialogContent
        className={cn("sm:max-w-lg", blocking && "[&>[data-close-button]]:hidden")}
        onInteractOutside={blocking ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            Política de Privacidade
          </DialogTitle>
          <DialogDescription className="text-center">
            Antes de continuar, precisamos do seu consentimento para tratar seus dados
            pessoais conforme a Lei Geral de Proteção de Dados (LGPD).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>
            Ao aceitar, você declara que leu e concorda com nossa{" "}
            <a
              href="/privacidade/politica-de-privacidade.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
            >
              Política de Privacidade
            </a>
            , incluindo:
          </p>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Coleta e tratamento de dados para gestão de campanhas e indicações</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Compartilhamento de dados com empresas parceiras para execução do serviço</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Armazenamento seguro e prazo de retenção conforme legislação</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Seus direitos de acesso, correção, exclusão e portabilidade dos dados</span>
            </li>
          </ul>
          <p className="text-xs">
            Você pode revogar seu consentimento a qualquer momento através das
            configurações de privacidade no seu perfil.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:justify-center">
          <Button className="w-full sm:w-auto" onClick={handleAccept}>
            Aceitar e Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
